import { z } from 'zod';

/**
 * Surge bulletin → EMERGING SITUATION BRIEF (ESB) — the LLM-summarized cluster
 * card contract. Born as the #65 surge bulletin (migration 0074): one
 * haiku-class summary of one igniter-qualified geo-cluster of GDELT headlines.
 * GROWN UP as the #295 Emerging Situation Brief (migration 0078): the same
 * card, now a CROSS-SOURCE FUSED, provenance-stamped brief.
 *
 * What the ESB adds over the v1 bulletin (all migration 0078, all nullable so a
 * pre-0078 bulletin still validates and the card degrades gracefully):
 *   • `isBrief` — discriminator: true for cross-source fused briefs, false /
 *     absent for legacy single-family bulletins.
 *   • `corroboration` — per-source-TYPE counts (news vs social vs official vs
 *     scanner): the brief SEPARATES what each source type independently says and
 *     counts corroboration BY type. Social alone is corroboration, NOT
 *     confirmation (the honesty bar).
 *   • `uncertaintyLine` — the explicit, never-inflated "not yet confirmed" line
 *     (council §3.6: uncertainty is a first-class field).
 *   • `leadingSignalAt` — the "first seen" stamp: the earliest fused-signal
 *     observation, present ONLY when the brief is a credible leading signal
 *     (coherent cluster AND no official feed yet). Null otherwise — precision
 *     over recall (#298).
 *   • `coherence` — the #298 disambiguation verdict ("are these N stories ONE
 *     real event, or did GDELT conflate Paris ON vs Paris FR?").
 *
 * The contract is deliberate about honesty:
 *   • the card always carries "AI-generated summary · N sources" chrome — a
 *     brief is never presented as reporting;
 *   • `evidence` is the snapshot of underlying signals the summary was
 *     generated FROM (now tagged with `sourceType`), rendered expandable beneath
 *     the card — the operator can always drop to sources;
 *   • `suppressed` marks a cluster the igniter silenced because officials are
 *     already reporting — still a real surge, framed as corroboration, never a
 *     second alarm;
 *   • `language` is the OUTPUT language — FR-dominant clusters get FR briefs.
 *
 * Generation failure (incl. the §3.7 hallucination-gate reject) writes no row —
 * the feed degrades to the raw cluster's signal cards; a missing brief never
 * blocks anything. NEVER auto-urgent, NEVER scores a Response Level — read-only.
 */

export const MEDIA_SIGNAL_BULLETIN_LANGUAGES = ['en', 'fr'] as const;
export type MediaSignalBulletinLanguage = (typeof MEDIA_SIGNAL_BULLETIN_LANGUAGES)[number];

/** The four source TYPES the brief fuses and counts independently (#295). News =
 *  geo-confirmed GDELT cluster; social = X/Bluesky chatter (geo-less, hazard+time
 *  matched); official = the suppression-guard / authoritative-feed context;
 *  scanner = #246, deferred (always absent in v1, reserved for forward-compat). */
export const MEDIA_SIGNAL_SOURCE_TYPES = ['news', 'social', 'official', 'scanner'] as const;
export type MediaSignalSourceType = (typeof MEDIA_SIGNAL_SOURCE_TYPES)[number];

/** One underlying signal as snapshotted at generation time. */
export const MediaSignalBulletinEvidenceSchema = z.object({
  /** media_signals.id of the story's representative row. */
  id: z.string(),
  source: z.string(),
  /** Distinct-origin identity (outlet domain / feed / handle). */
  origin: z.string(),
  title: z.string().nullable(),
  url: z.string().nullable(),
  /** ISO-8601 — COALESCE(published_at, ingested_at). */
  observedAt: z.string(),
  geoName: z.string().nullable(),
  /** The fused source TYPE this evidence item belongs to (#295). Optional so a
   *  pre-0078 bulletin's untagged evidence still validates (read as 'news'). */
  sourceType: z.enum(MEDIA_SIGNAL_SOURCE_TYPES).optional(),
});
export type MediaSignalBulletinEvidence = z.infer<typeof MediaSignalBulletinEvidenceSchema>;

/** Per-source-type tally: distinct stories/posts + distinct origins. */
export const MediaSignalBriefTallySchema = z.object({
  count: z.number().int().nonnegative(),
  origins: z.number().int().nonnegative(),
});
export type MediaSignalBriefTally = z.infer<typeof MediaSignalBriefTallySchema>;

/**
 * The cross-source corroboration block (#295) — counts BY source type, computed
 * deterministically in code (never the LLM, so the counts can't be
 * hallucinated). The brief's "show your work" core.
 */
export const MediaSignalBriefCorroborationSchema = z.object({
  /** Geo-confirmed news — the GDELT cluster (city/region stories). */
  news: MediaSignalBriefTallySchema,
  /** X + Bluesky chatter, hazard + time matched (NOT location-confirmed). */
  social: MediaSignalBriefTallySchema,
  /** The official context: present/absent + how many authoritative feed items. */
  official: z.object({
    present: z.boolean(),
    /** Authoritative (tier='official') rss signals of the same hazard in the window. */
    count: z.number().int().nonnegative(),
    /** Human-readable official-context reason (the suppression-guard reason), if any. */
    reason: z.string().nullable(),
  }),
  /** Scanner (#246) — deferred; always absent in v1. Kept for forward-compat. */
  scanner: z.object({
    present: z.boolean(),
    note: z.string(),
  }),
});
export type MediaSignalBriefCorroboration = z.infer<typeof MediaSignalBriefCorroborationSchema>;

/**
 * The #298 cluster-coherence / GDELT-disambiguation verdict. A PRECISION
 * INCREASER (not a recall-lowering gate): 'coherent' = the cluster's place names
 * agree (one event); 'ambiguous' = explicit province tags disagree across the
 * cluster (a GDELT conflation signature, e.g. Chatham ON vs Chatham NB) — when
 * ambiguous the leading-signal stamp is withheld.
 */
export const MediaSignalBriefCoherenceSchema = z.object({
  verdict: z.enum(['coherent', 'ambiguous']),
  /** Distinct explicit province codes the cluster's place names resolved to. */
  provinces: z.array(z.string()),
  note: z.string().nullable(),
});
export type MediaSignalBriefCoherence = z.infer<typeof MediaSignalBriefCoherenceSchema>;

export const MediaSignalBulletinSchema = z.object({
  id: z.string().uuid(),
  /** The igniter's stable cluster identity (media:<hazard>:<latCell>:<lonCell>). */
  clusterKey: z.string(),
  /** The raised queue row, when one exists; null for suppressed clusters. */
  anomalyId: z.string().uuid().nullable(),
  /** The lifecycle status of the surge anomaly this brief belongs to (#561),
   *  joined at read time from media_signal_anomalies. Drives the pin lifecycle:
   *  a brief stays pinned while its anomaly is LIVE ('active' | 'confirmed');
   *  once it 'resolved' (the 35-min chatter TTL) or was 'dismissed', the brief
   *  unpins and takes its chronological place in the stream. Null when no anomaly
   *  is linked (a suppressed cluster raised no queue row) or the FK was set-null
   *  by an anomaly prune — fail-safe UNPINNED by construction. Optional so a
   *  pre-#561 bulletin literal still validates. */
  anomalyStatus: z.enum(['active', 'confirmed', 'dismissed', 'resolved']).nullable().optional(),
  hazardKey: z.string(),
  language: z.enum(MEDIA_SIGNAL_BULLETIN_LANGUAGES),
  summary: z.string(),
  suppressed: z.boolean(),
  suppressionReason: z.string().nullable(),
  /** Cluster centroid (honest geo — centroid of source-provided points). */
  geo: z.object({
    lon: z.number().min(-180).max(180),
    lat: z.number().min(-90).max(90),
  }),
  radiusM: z.number(),
  /** Distinct geo-confirmed news stories (the N in "· N sources" chrome reads sourceCount). */
  signalCount: z.number(),
  /** Distinct upstream news origins (outlets/feeds). */
  sourceCount: z.number(),
  evidence: z.array(MediaSignalBulletinEvidenceSchema),
  /** Generation audit — which model wrote this summary. */
  model: z.string(),
  generatedAt: z.string(),
  // ── Emerging Situation Brief fields (#295, migration 0078) ─────────────────
  // Optional (not defaulted) so a legacy bulletin literal / pre-0078 row stays a
  // valid MediaSignalBulletin; the API always sends them (value or null) and
  // every consumer guards with `?.` / truthy / `!= null`.
  /** True for a cross-source FUSED brief; false/absent for a legacy bulletin. */
  isBrief: z.boolean().optional(),
  /** Per-source-TYPE corroboration counts (#295). Null on legacy bulletins. */
  corroboration: MediaSignalBriefCorroborationSchema.nullable().optional(),
  /** The explicit, never-inflated "not yet confirmed" line (#295 §3.6). */
  uncertaintyLine: z.string().nullable().optional(),
  /** Earliest fused-signal observation — the "first seen" leading-signal stamp
   *  (#298). Null when suppressed or coherence is ambiguous (precision over recall). */
  leadingSignalAt: z.string().nullable().optional(),
  /** Cluster coherence / GDELT-disambiguation verdict (#298). Null on legacy. */
  coherence: MediaSignalBriefCoherenceSchema.nullable().optional(),
});
export type MediaSignalBulletin = z.infer<typeof MediaSignalBulletinSchema>;

export const MediaSignalBulletinListResponseSchema = z.object({
  bulletins: z.array(MediaSignalBulletinSchema),
});
export type MediaSignalBulletinListResponse = z.infer<
  typeof MediaSignalBulletinListResponseSchema
>;
